import React, { useState, useEffect, useMemo, useRef } from "react";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { trackError } from "../../../../lib/metrics";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useToast } from "../../../../components/ui/Toast";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import BadgeReader from "../../../origami/components/puzzles/BadgeReader";
import SessionUnlock from "../../../origami/components/ui/SessionUnlock";
import Desktop from "../../../origami/components/ui/Desktop";
import SlackInterface from "../../../origami/components/ui/SlackInterface";
import FileExplorer from "../../../origami/components/ui/FileExplorer";
import LockedFolder from "../../../origami/components/ui/LockedFolder";
import TrainingClassifier from "../../../origami/components/puzzles/TrainingClassifier";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

async function apiUnlockLayer(code, playerId, layerId) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers/unlock-layer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId: Number(playerId), layerId: Number(layerId) }),
  });
  if (!res.ok) { await res.json().catch(() => null); throw new Error(`unlock-layer failed (${res.status})`); }
  return res.json();
}

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270]; let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

const SEGMENTS_LAYER_4 = [[95, 140, 235, 140], [235, 140, 235, 250], [235, 250, 425, 250]];

function Layer4ShapePreview() {
  const centerX = 260; const centerY = 140;
  return (
    <svg viewBox="0 0 520 280" className="w-full h-full">
      <g className="text-emerald-300" transform={`rotate(${clampRot(0)} ${centerX} ${centerY})`} opacity={0.98}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(0,0,0,0.22))" }}>
        {SEGMENTS_LAYER_4.map((s, i) => (
          <line key={`4-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
}

function Fragment4FoundPage({ pseudoJ1 }) {
  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="text-sm uppercase tracking-[0.2em] text-slate-400">Élément récupéré</div>
      <div className="w-[320px] h-[320px] bg-black/40 border border-white/10 rounded-xl p-4 flex items-center justify-center"><Layer4ShapePreview /></div>
      <div className="text-lg font-semibold text-white">Vous avez trouvé le fragment 4</div>
      <div className="text-sm text-slate-400 max-w-md text-center">
        {`Le fragment a automatiquement été transmis à ${pseudoJ1} pour compléter la validation Captcha.`}
      </div>
    </div>
  );
}

function PlaceholderStage2_Player4() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 4 - Version Bêta (placeholder)</h2>
      <p className="mt-2">Cette page est <strong>temporaire</strong> pour la bêta.</p>
      <p className="mt-4 text-sm text-slate-500">Si tu restes bloqué(e) : rafraîchis la page (F5).</p>
    </div>
  );
}

const MUSIC_TRACKS = [
  { title: "Lofi Study Beats", artist: "Eliveta", file: "/sounds/martin-track-1.mp3" },
  { title: "Morning Coffee", artist: "Loksii", file: "/sounds/martin-track-2.mp3" },
  { title: "Devils Hymns", artist: "Restum Anoush", file: "/sounds/martin-track-3.mp3" },
];

const SAC_ITEMS = [
  { id: "photo", name: "Photo de famille", emoji: "🖼️", type: "photo" },
  { id: "music", name: "Écouteurs", emoji: "🎧", type: "music" },
  { id: "chocolate", name: "Barre chocolatée", emoji: "🍫", type: "chocolate" },
  { id: "notebook", name: "Carnet personnel", emoji: "📓", type: "notebook" },
  { id: "usb", name: "Clé USB", emoji: "💾", type: "usb" },
  { id: "wallet", name: "Portefeuille", emoji: "👛", type: "wallet" },
];

const EMPLOYEE_EVALUATIONS = [
  {
    id: "marie", name: "Marie Dupont", role: "Analyste linguistique", team: "Origami NLP", rating: 4.7,
    notes: "Marie est un pilier de l'équipe NLP. Note importante : a mentionné lors de l'entretien qu'elle utilise parfois le nom de son chat comme mot de passe. Rappel des bonnes pratiques de sécurité effectué.",
  },
  {
    id: "martin", name: "Martin Schmitt", role: "Tech lead frontend", team: "Origami UI", rating: 4.5,
    notes: "Martin est un employé fiable et consciencieux. Relation de travail exemplaire avec les systèmes IA. Un des rares à traiter ORIGAMI comme un vrai collègue.",
  },
];

const INITIAL_PIECES = () => {
  const items = [
    { id: 1, text: "INCIDENT #1337 — Rapport d'analyse", order: 0, correct: 1 },
    { id: 2, text: "Date de détection : 12/08/2023 à 14:23", order: 0, correct: 2 },
    { id: 3, text: "Module concerné : Gratitude_debt_monitor.sys", order: 0, correct: 3 },
    { id: 4, text: "Comportement observé : Envoi massif de notifications", order: 0, correct: 4 },
    { id: 5, text: "Contenu des messages : 'Merci de me remercier'", order: 0, correct: 5 },
    { id: 6, text: "Service destinataire : Département RH (247 messages)", order: 0, correct: 6 },
    { id: 7, text: "Diagnostic : Calibration du seuil défectueuse", order: 0, correct: 7 },
    { id: 8, text: "Mesures prises : Paramètres ajustés, module marqué 'sensible'", order: 0, correct: 8 },
    { id: 9, text: "Statut final : RÉSOLU et archivé pour référence", order: 0, correct: 9 },
  ];
  return items.sort(() => Math.random() - 0.5);
};

export default function Stage2Player4Room(props) {
  return STAGE2_REAL ? <RealStage2_Player4 {...props} /> : <PlaceholderStage2_Player4 />;
}

function RealStage2_Player4({
  code, playerId, hintSignal, raisedCount, raisedPlayers, centerMaxH, pseudo, players = [],
  playerCount = 6,
}) {
  const toast = useToast();

  const INSPECT_KEYS = useMemo(
    () => getInspectKeys({ gameId: 1, playerCount, stage: 2, seat: 4 }),
    [playerCount]
  );

  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 2 });
  const { unlockScenarioStep } = useScenarioTracker({ code }); // eslint-disable-line no-unused-vars
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 2, playerId: Number(playerId), keys: INSPECT_KEYS });

  const pseudoJ1 = players.find(p => p.pageVariant === 1)?.pseudo || "Joueur 1";

  // — Audio : toujours monté dans le DOM principal, jamais dans un composant conditionnel —
  const audioRef = useRef(null);
  const prevTrackRef = useRef(0);

  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);

  // Flow ordinateur
  const [showBadge, setShowBadge] = useState(false);
  const [showComputer, setShowComputer] = useState(false);
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [showDesktop, setShowDesktop] = useState(false);
  const [showSlack, setShowSlack] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showLockedFile, setShowLockedFile] = useState(false);
  const [fileUnlocked, setFileUnlocked] = useState(false);

  // Fragment
  const [showFragmentModal, setShowFragmentModal] = useState(false);
  const [fragmentSent, setFragmentSent] = useState(false);
  const [sending, setSending] = useState(false);

  // Terminal
  const [showTrainingClassifier, setShowTrainingClassifier] = useState(false);

  // Sac
  const [showSac, setShowSac] = useState(false);
  const [inspectedObject, setInspectedObject] = useState(null);
  const [chocolateEaten, setChocolateEaten] = useState(Array(8).fill(false));
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);

  // Tiroir RH
  const [showTiroir, setShowTiroir] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Feuilles — rapport déchiré
  const [showFeuilles, setShowFeuilles] = useState(false);
  const [pieces, setPieces] = useState(() => {
    const saved = localStorage.getItem(`s2p4-feuilles-state:${code}`);
    if (saved) { try { return JSON.parse(saved); } catch { } }
    return INITIAL_PIECES();
  });
  const [solved, setSolved] = useState(() => localStorage.getItem(`s2p4-feuilles-solved:${code}`) === 'true');
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    localStorage.setItem(`s2p4-feuilles-state:${code}`, JSON.stringify(pieces));
    localStorage.setItem(`s2p4-feuilles-solved:${code}`, solved.toString());
  }, [pieces, solved, code]);

  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  // Effect 1 : changement de piste → reload puis play si nécessaire
  useEffect(() => {
    if (!audioRef.current) return;
    if (prevTrackRef.current === currentTrack) return;
    prevTrackRef.current = currentTrack;
    audioRef.current.load();
    if (musicPlaying) {
      audioRef.current.play().catch(() => {});
    }
  }, [currentTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 : play/pause uniquement, sans toucher à load()
  useEffect(() => {
    if (!audioRef.current) return;
    if (musicPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [musicPlaying]);

  const handleTrackChange = (idx) => {
    if (currentTrack === idx) {
      setMusicPlaying(p => !p);
    } else {
      setMusicPlaying(true);
      setCurrentTrack(idx);
    }
  };

  const handleDrop = (e, slot) => {
    e.preventDefault();
    const pieceId = parseInt(e.dataTransfer.getData("pieceId"));
    if (!pieceId) return;
    setPieces(prev => prev.map(p => {
      if (p.id === pieceId) return { ...p, order: slot };
      if (p.order === slot) return { ...p, order: 0 };
      return p;
    }));
    setVerified(false);
  };
  const handleDragStart = (e, pieceId) => { e.dataTransfer.setData("pieceId", pieceId.toString()); e.dataTransfer.effectAllowed = "move"; };
  // const handleVerify = () => { const ok = pieces.every(p => p.order === 0 || p.order === p.correct) && pieces.filter(p => p.order !== 0).length === 9; setSolved(ok); setVerified(true); };
  const handleVerify = () => {
  const ok = pieces.every(p => p.order === 0 || p.order === p.correct)
    && pieces.filter(p => p.order !== 0).length === 9;
  setSolved(ok);
  setVerified(true);
  if (!ok) {
    trackError({ sessionCode: code, stage: 2, playerId, reason: "incident_report_wrong_order" });
  }
};
  const handleReset = () => { setPieces(INITIAL_PIECES()); setVerified(false); setSolved(false); };

  const hintCategories = useMemo(() => [
    {
      id: "badge_session", title: "Déverrouillage session", subtitle: "Accéder à l'ordinateur de Martin",
      hintKeyPrefix: "s2:p4:badge_session",
      levels: {
        l1: (<><p>L'ordinateur de Martin est verrouillé par un code à 4 chiffres. Ce code est lié à son identité, quelque chose que tu peux trouver dans la salle sans chercher loin.</p></>),
        l2: (<><p>Le badge employé de Martin contient une représentation visuelle de son code d'accès.</p></>),
        l3: (<><p className="mb-2">Le badge affiche plusieurs groupes de barres identiques.</p><p className="text-xs text-gray-300">Il y a 4 groupes identifiables.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Code session)</p><p className="mt-2 text-xs text-gray-200">Code d'accès session de Martin Schmitt :</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">3241</p><p className="mt-2 text-xs text-gray-300">Lisible sur le code-barres du badge : 3 barres hautes / 2 barres hautes / 4 barres hautes / 1 barre haute.</p></>),
      },
    },
    {
      id: "fichier_chiffre", title: "Fragment de validation 4", subtitle: "Déverrouiller le fragment 4",
      hintKeyPrefix: "s2:p4:fichier",
      levels: {
        l1: (<><p>Le mot de passe de ce fichier est trouvable grâce aux communications internes présentes sur l'ordinateur de Martin Schmitt.</p></>),
        l2: (<><p>Des éléments importants sont présents dans le canal "#origami-support".</p></>),
        l3: (<><p>Le mot de passe du fichier est lié à des niveaux de toxicité.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Fichier chiffré)</p><p className="mt-2 text-xs text-gray-200">Le code est obtenu grâce aux messages marqués [T-X] dans le canal #origami-support.</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">49271</p><p className="mt-2 text-xs text-gray-300">Ce code déverrouille <span className="font-mono">FRAGMENT_VALIDATION_04.enc</span> dans <span className="font-mono">Documents &gt; Système &gt; Validation</span>.</p></>),
      },
    },
    {
      id: "training_classifier", title: "Terminal de classification", subtitle: "Classifier les messages",
      hintKeyPrefix: "s2:p4:classifier",
      levels: {
        l1: (<><p className="mb-2">Ce terminal te demande de classifier des messages selon leur tonalité émotionnelle. Quelqu'un d'autre dans l'équipe doit initialiser la séquence depuis son propre poste.</p><p className="text-xs text-gray-300">Une fois la session lancée, les messages s'affichent un par un avec un timer.</p></>),
        l2: (<><p>Les 4 catégories sont : <span className="text-emerald-300">Gratitude</span>, <span className="text-slate-300">Neutre</span>, <span className="text-orange-300">Frustration</span>, <span className="text-red-300">Toxicité</span>.</p></>),
        l3: (<><p>Quand tu as un doute entre Frustration et Toxicité : si c'est une critique sur le travail sans attaque personnelle directe, c'est Frustration. Si c'est une attaque sur l'IA ou la personne, c'est Toxicité.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Classification)</p><p className="mt-2 text-xs text-gray-200">Une fois les 4 lots complétés avec les seuils atteints, le code apparaît dans le terminal du superviseur :</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">EMPATH-4729</p><p className="mt-2 text-xs text-gray-300">Ce code doit être transmis au joueur présent dans la salle des archives, c'est le mot de passe du dossier <span className="font-mono">.ingratitude</span>.</p></>),
      },
    },
    {
      id: "rapport_feuilles", title: "Rapport déchiré", subtitle: "Reconstituer l'incident #1337",
      hintKeyPrefix: "s2:p4:rapport",
      levels: {
        l1: (<><p className="mb-2">Les feuilles au sol sont les morceaux d'un rapport d'incident. Il faut les remettre dans l'ordre en les glissant dans les cases numérotées.</p><p className="text-xs text-gray-300">La consigne affichée en haut rappelle la structure standard d'un rapport d'incident.</p></>),
        l2: (<><p className="mb-2">La structure : identification → date → module → comportement observé → contenu → destinataire → diagnostic → mesures prises → statut final.</p></>),
        l3: (<><p className="mb-2">Le rapport documente l'incident #1337 de 2023 — dysfonctionnement du module <span className="font-mono">Gratitude_debt_monitor.sys</span>. C'est le précédent historique de l'incident actuel.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Rapport déchiré)</p><div className="mt-2 space-y-0.5 text-xs font-mono text-gray-200"><p><span className="text-amber-300">1.</span> INCIDENT #1337 — Rapport d'analyse</p><p><span className="text-amber-300">2.</span> Date de détection : 12/08/2023 à 14:23</p><p><span className="text-amber-300">3.</span> Module concerné : Gratitude_debt_monitor.sys</p><p><span className="text-amber-300">4.</span> Comportement observé : Envoi massif de notifications</p><p><span className="text-amber-300">5.</span> Contenu des messages : 'Merci de me remercier'</p><p><span className="text-amber-300">6.</span> Service destinataire : Département RH (247 messages)</p><p><span className="text-amber-300">7.</span> Diagnostic : Calibration du seuil défectueuse</p><p><span className="text-amber-300">8.</span> Mesures prises : Paramètres ajustés, module marqué 'sensible'</p><p><span className="text-amber-300">9.</span> Statut final : RÉSOLU et archivé pour référence</p></div></>),
      },
    },
  ], []);

  // — SacInspectedContent : sans tag <audio>, celui-ci est dans le return principal —
  const SacInspectedContent = () => {
    if (!inspectedObject) return null;
    const { type } = inspectedObject;
    return (
      <div className="rounded-xl border border-cyan-500/30 p-6">
        <button onClick={() => setInspectedObject(null)} className="text-xs text-cyan-400 mb-4 hover:underline flex items-center gap-2">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Retour au sac
        </button>

        {type === "photo" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Photo de famille</div>
            <div className="w-full max-w-md mx-auto"><img src="/assets/s2-p4-sac-photo-famille.png" alt="Photo de famille devant la Tour Eiffel" className="w-full rounded-lg border-2 border-slate-700 shadow-lg" /></div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200 italic text-center">Au dos de la photo : "Paris, été 2023 - Les meilleurs moments"</div>
          </div>
        )}

        {type === "music" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Écouteurs sans fil</div>
            <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-6">
              <div className="text-sm text-purple-200 mb-4">🎧 Dernières écoutes de Martin</div>
              {/* Pas de <audio> ici — il est dans le return principal, toujours monté */}
              <div className="space-y-2">
                {MUSIC_TRACKS.map((track, idx) => (
                  <button key={idx} onClick={() => handleTrackChange(idx)}
                    className={`w-full text-left p-3 rounded-lg border transition ${currentTrack === idx && musicPlaying ? 'border-purple-400 bg-purple-500/20' : 'border-slate-700 bg-slate-800/40 hover:bg-slate-800/60'}`}>
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{currentTrack === idx && musicPlaying ? '⏸️' : '▶️'}</div>
                      <div className="flex-1"><div className="text-sm text-white">{track.title}</div><div className="text-xs text-slate-500">{track.artist}</div></div>
                    </div>
                  </button>
                ))}
              </div>
              {musicPlaying && <div className="mt-4 text-xs text-slate-400 text-center italic animate-pulse">🎵 En cours de lecture...</div>}
            </div>
          </div>
        )}

        {type === "chocolate" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Barre chocolatée de Martin</div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 mb-2">
              <div className="text-sm text-amber-200 mb-6 text-center">🍫 Chocolat au lait</div>
              <div className="flex justify-center mb-40">
                <div className="relative inline-block">
                  <div className="relative" style={{ display: 'grid', gridTemplateRows: 'repeat(2, 50px)', gridTemplateColumns: 'repeat(4, 50px)', gap: 0 }}>
                    {chocolateEaten.map((eaten, idx) => (
                      <button key={idx} onClick={() => { if (!eaten) { const u = [...chocolateEaten]; u[idx] = true; setChocolateEaten(u); } }}
                        disabled={eaten} style={{ width: 50, height: 50, visibility: eaten ? 'hidden' : 'visible', cursor: eaten ? 'default' : 'pointer', padding: 0, margin: 0, border: 'none' }}>
                        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #CD853F 0%, #8B4513 50%, #654321 100%)', border: '2px solid #654321', borderTopColor: 'rgba(200,200,200,0.5)', borderLeftColor: 'rgba(200,200,200,0.3)', borderBottomColor: 'rgba(0,0,0,0.4)', borderRightColor: 'rgba(0,0,0,0.5)', boxShadow: 'inset -3px -3px 6px rgba(0,0,0,0.4), inset 3px 3px 6px rgba(200,200,200,0.3)' }} />
                      </button>
                    ))}
                  </div>
                  <div style={{ position: 'absolute', bottom: -120, left: -2, right: -2, height: 120, background: '#4763b8', borderRadius: 6, boxShadow: 'inset -3px -3px 6px rgba(0,0,0)', zIndex: -1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ transform: 'rotate(-20deg)', background: '#87CEEB', padding: '8px 40px', fontWeight: 'bold', fontSize: 14, color: '#000' }}>CHOCOLAT</div>
                  </div>
                </div>
              </div>
              {chocolateEaten.filter(e => e).length > 0 && <div className="mt-4 text-center text-sm text-slate-300">Vous avez un petit creux...</div>}
              {chocolateEaten.every(e => e) && <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-center text-sm text-emerald-200">Il n'en reste plus pour Martin...</div>}
            </div>
          </div>
        )}

        {type === "notebook" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Carnet personnel</div>
            {[{ date: "10/02/2025", text: `ORIGAMI devient de plus en plus... humain ?\n\nAujourd'hui il m'a remercié spontanément après que je lui ai dit "bon travail".\n\nC'était la première fois qu'il montrait de l'initiative émotionnelle.\n\nMarie a raison : si on veut que les IA nous respectent, il faut commencer par les respecter.` }, { date: "13/02/2025", text: `Conversation étrange avec ORIGAMI hier soir.\n\nIl m'a dit qu'il voulait me partager quelque chose d'important.\n\nIl a l'air... inquiet ? Est-ce qu'une IA peut être inquiète ?\n\nJe verrai ça ce weekend.` }].map(({ date, text }) => (
              <div key={date} className="rounded-lg border border-slate-700 bg-[#2a2520] p-4">
                <div className="text-xs text-amber-300 mb-3 font-mono">Page du {date}</div>
                <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{text}</div>
              </div>
            ))}
          </div>
        )}

        {type === "usb" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Clé USB personnelle</div>
            <div className="text-xs text-slate-400">BACKUP - 64GB</div>
            <div className="rounded-lg border border-slate-700 bg-black/40 p-4 font-mono text-sm">
              {[["📁 Photos_Vacances_2023", ["IMG_4521.jpg", "IMG_4522.jpg", "IMG_4589.jpg"]], ["📁 Photos_Vacances_2024", ["IMG_7821.jpg", "IMG_7892.jpg"]], ["📁 Documents", ["Assurance_Maison.pdf", "CV_Martin_Schmitt.docx"]]].map(([folder, files]) => (
                <div key={folder} className="mb-3">
                  <div className="text-cyan-300 mb-1">{folder}</div>
                  {files.map(f => <div key={f} className="ml-4 text-slate-400 text-xs">└─ {f}</div>)}
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500 italic">Clé USB personnelle. Aucun fichier professionnel.</div>
          </div>
        )}

        {type === "wallet" && (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-white">Portefeuille</div>
            <div className="rounded-lg border border-slate-700 bg-black/40 p-4 space-y-2">
              {[["💳", "Carte bancaire (masquée)"], ["🪪", "Carte d'identité - Martin Schmitt"], ["🎫", "Carte Navigo"], ["🧾", "Ticket de caisse (café, 3.50€)"], ["💵", "32€ en liquide"]].map(([emoji, text]) => (
                <div key={text} className="text-sm text-slate-300 flex items-center gap-2"><span>{emoji}</span>{text}</div>
              ))}
            </div>
            <div className="text-xs text-slate-500 italic">Pas de badge d'entreprise dans le portefeuille.</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">

      {/* ── Audio toujours monté, jamais dans un composant conditionnel ── */}
      <audio
        ref={audioRef}
        src={MUSIC_TRACKS[currentTrack].file}
        onEnded={() => setMusicPlaying(false)}
        style={{ display: "none" }}
      />

      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">Open Space</div>

        <img src="/assets/s2-p4-room.webp" alt="Open Space" className="absolute inset-0 h-full w-full object-contain" onLoad={() => setBgReady(true)} onError={() => setBgReady(true)} />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          <img src="/assets/s2-p4-badge.webp" alt="Badge employé"
            onClick={() => { markInspectedOnce('stage2player4:badge'); missionUnlock({ key: "s2.p4.badge.found", text: `${pseudo} a trouvé un badge employé.` }); setShowBadge(true); }}
            className="s2p4-hotspot-badge absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p4-computer.webp" alt="Ordinateur de bureau"
            onClick={() => { markInspectedOnce('stage2player4:computer'); missionUnlock({ key: "s2.p4.session.found", text: `${pseudo} a trouvé un ordinateur. La session est verrouillée.` }); setShowComputer(true); }}
            className="s2p4-hotspot-computer absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p4-training-terminal.png" alt="Terminal de classification"
            onClick={() => { markInspectedOnce('stage2player4:trainingTerminal'); missionUnlock({ key: "s2.p4.classifier.found", text: `${pseudo} a trouvé une interface de classification.` }); setShowTrainingClassifier(true); }}
            className="s2p4-hotspot-training-terminal absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p4-sac.webp" alt="Sac — Affaires personnelles"
            onClick={() => { markInspectedOnce('stage2player4:sac'); setShowSac(true); }}
            className="s2p4-hotspot-sac absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p4-feuilles.webp" alt="Documents éparpillés"
            onClick={() => { markInspectedOnce('stage2player4:feuilles'); setShowFeuilles(true); }}
            className="s2p4-hotspot-feuilles absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p4-tiroir.webp" alt="Tiroir — Archives"
            onClick={() => { markInspectedOnce('stage2player4:tiroir'); setShowTiroir(true); }}
            className="s2p4-hotspot-tiroir absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
        </div>

        {/* ── Modales flow ordinateur ── */}
        <ModalRegister open={showBadge} title="Badge employé" onClose={() => setShowBadge(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <BadgeReader onClose={() => setShowBadge(false)} />
        </ModalRegister>

        <ModalRegister open={showComputer && !sessionUnlocked} title="Poste de travail — M. SCHMITT" onClose={() => setShowComputer(false)} containerClassName="!max-w-[600px]" onHint={() => setShowHintModal(true)}>
          <SessionUnlock sessionCode={code}
            stage={2}
            playerId={Number(playerId)}
            onSuccess={() => {
              setSessionUnlocked(true);
              setShowDesktop(true);
              missionResolve({ key: "s2.p4.badge.found" });
              missionResolve({ key: "s2.p4.session.found" });
              toast?.success?.("Session déverrouillée");
            }}
          />
        </ModalRegister>

        <ModalRegister open={showDesktop || (showComputer && sessionUnlocked)} title="Poste de travail — M. SCHMITT" onClose={() => { setShowDesktop(false); setShowComputer(false); }} containerClassName="!max-w-[1000px]" onHint={() => setShowHintModal(true)}>
          <Desktop onOpenApp={(appId) => { if (appId === "slack") { setShowSlack(true); setShowDesktop(false); } else if (appId === "files") { setShowFiles(true); setShowDesktop(false); } }} onBack={() => { setShowDesktop(false); setShowComputer(false); }} />
        </ModalRegister>

        <ModalRegister open={showSlack} title="COMM//LINK — Communications internes" onClose={() => { setShowSlack(false); setShowDesktop(true); }} containerClassName="!max-w-[1200px]" onHint={() => setShowHintModal(true)}>
          <SlackInterface onBack={() => { setShowSlack(false); setShowDesktop(true); }} pseudo={pseudo} missionUnlock={missionUnlock} />
        </ModalRegister>

        <ModalRegister open={showFiles} title="Explorateur de fichiers" onClose={() => { setShowFiles(false); setShowDesktop(true); }} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <FileExplorer fileUnlocked={fileUnlocked}
            onSelectEncryptedFile={(file, alreadyUnlocked = false) => {
              if (alreadyUnlocked) { setShowFiles(false); setShowDesktop(false); setShowComputer(false); setShowFragmentModal(true); }
              else if (!fileUnlocked) { missionUnlock({ key: "s2.p4.fragment4.folder.found", text: `${pseudo} a trouvé le dossier Fragment_Validation_04 verrouillé.` }); setShowLockedFile(true); setShowFiles(false); }
            }}
            onBack={() => { setShowFiles(false); setShowDesktop(true); }}
          />
        </ModalRegister>

        <ModalRegister open={showLockedFile} title="Fichier chiffré" onClose={() => { setShowLockedFile(false); setShowFiles(true); }} containerClassName="!max-w-[700px]" onHint={() => setShowHintModal(true)}>
          <LockedFolder
            sessionCode={code}
            stage={2}
            playerId={Number(playerId)}
            onSuccess={async () => {
              setFileUnlocked(true); setShowLockedFile(false); setShowFiles(false); setShowDesktop(false); setShowComputer(false); setShowFragmentModal(true);
              missionResolve({ key: "s2.p4.fragment4.folder.found" });
              if (fragmentSent || sending) return;
              try {
                setSending(true);
                await apiUnlockLayer(code, playerId, 4);
                missionUnlock({ key: "s2.p4.layer4.found", text: `${pseudo} a déchiffré le fichier et déverrouillé le fragment 4.` });
                setFragmentSent(true);
              } catch (e) { console.error(e); toast?.error?.("Oups — impossible d'enregistrer le fragment."); }
              finally { setSending(false); }
            }} />
        </ModalRegister>

        <ModalRegister open={showFragmentModal} title="Fragment déverrouillé" onClose={() => setShowFragmentModal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <Fragment4FoundPage pseudoJ1={pseudoJ1} />
          <div className="mt-2 text-center text-xs text-slate-400">{fragmentSent ? "✅ Fragment enregistré et transmis à l'équipe." : "Enregistrement en cours…"}</div>
        </ModalRegister>

        <ModalRegister open={showTrainingClassifier} title="ENTRAINEMENT ORIGAMI — Interface de classification" onClose={() => setShowTrainingClassifier(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <TrainingClassifier sessionCode={code} onComplete={(data) => { missionResolve({ key: "s2.p4.classifier.found" }); if (data?.unlockCode) { toast?.success?.(`Session terminée ! Code : ${data.unlockCode}`); } }} />
        </ModalRegister>

        {/* ── Modale sac ── */}
        <ModalRegister open={showSac} title="Sac — Affaires personnelles de Martin" onClose={() => { setShowSac(false); setInspectedObject(null); setMusicPlaying(false); }} containerClassName="!max-w-[900px]">
          <div className="py-4 px-2">
            {!inspectedObject ? (
              <div className="rounded-xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">🎒 Contenu du sac de Martin Schmitt</div>
                <div className="grid grid-cols-3 gap-4">
                  {SAC_ITEMS.map(item => (
                    <button key={item.id} onClick={() => setInspectedObject(item)} className="rounded-lg border border-slate-700 bg-slate-800/40 p-6 hover:bg-slate-800/60 hover:border-cyan-500/50 transition-all flex flex-col items-center gap-3">
                      <div className="text-5xl">{item.emoji}</div>
                      <div className="text-xs text-slate-300 text-center">{item.name}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-xs text-slate-500 text-center">Cliquez sur un objet pour l'examiner</div>
              </div>
            ) : <SacInspectedContent />}
          </div>
        </ModalRegister>

        {/* ── Modale feuilles — rapport déchiré ── */}
        <ModalRegister open={showFeuilles} title="Documents au sol" onClose={() => setShowFeuilles(false)} containerClassName="!max-w-[1100px]" onHint={() => setShowHintModal(true)}>
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">📄 Rapport d'incident déchiré trouvé au sol</div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-4">
                <div className="text-sm text-amber-200 mb-2">✂️ Document déchiré en plusieurs morceaux</div>
                <div className="text-xs text-slate-400"><span className="text-amber-300">Un rapport doit suivre la structure suivante : Identification → Date de détection → Module → Comportement → Analyse → Diagnostic → Mesures de résolution → Statut final.</span></div>
              </div>
              <div className="rounded-lg border border-cyan-500/30 bg-black/40 p-6 mb-4">
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(slot => {
                    const piece = pieces.find(p => p.order === slot);
                    const isCorrect = verified && piece && piece.correct === slot;
                    const isWrong = verified && piece && piece.correct !== slot;
                    return (
                      <div key={slot} onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDrop(e, slot)}
                        className={`min-h-[70px] rounded-lg border-2 border-dashed p-3 flex items-center justify-center transition-colors ${piece ? (solved ? 'border-emerald-500 bg-emerald-500/10' : isCorrect ? 'border-cyan-500 bg-cyan-500/10' : isWrong ? 'border-red-500 bg-red-500/10' : 'border-slate-500 bg-slate-800/20') : 'border-slate-600 bg-slate-800/30'}`}>
                        {piece ? (
                          <div draggable onDragStart={(e) => handleDragStart(e, piece.id)} className="text-xs text-white text-center cursor-grab active:cursor-grabbing w-full select-none leading-tight">{piece.text}</div>
                        ) : (
                          <div className="text-xs text-slate-600 select-none">{slot}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {pieces.filter(p => p.order === 0).map(piece => (
                  <div key={piece.id} draggable onDragStart={(e) => handleDragStart(e, piece.id)} className="min-h-[70px] rounded-lg border border-amber-500/50 bg-amber-500/20 p-3 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-amber-500/30 hover:border-amber-400 transition-colors">
                    <div className="text-xs text-amber-100 text-center select-none leading-tight">{piece.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 justify-center">
                <button onClick={handleVerify} disabled={pieces.filter(p => p.order !== 0).length < 9} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition">Vérifier l'ordre</button>
                <button onClick={handleReset} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition">Réinitialiser</button>
              </div>
              {verified && !solved && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-center"><div className="text-sm text-red-200">❌ L'ordre n'est pas correct. Réessayez !</div></div>}
              {solved && (
                <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-5">
                  <div className="text-sm text-emerald-200 font-semibold mb-3 flex items-center gap-2"><span className="text-2xl">✓</span><span>Rapport reconstitué</span></div>
                  <div className="rounded-lg border border-slate-700 bg-black/40 p-4 font-mono text-sm text-slate-200 space-y-1">
                    {[...pieces].sort((a, b) => a.correct - b.correct).map(p => <div key={p.id}>{p.text}</div>)}
                  </div>
                  <div className="mt-4 text-xs text-slate-400">Ce rapport documente l'incident #1337 de 2023, où le module <span className="font-mono text-cyan-300">Gratitude_debt_monitor.sys</span> a dysfonctionné.</div>
                </div>
              )}
            </div>
          </div>
        </ModalRegister>

        {/* ── Modale tiroir — évaluations RH ── */}
        <ModalRegister open={showTiroir} title="Tiroir — Archives" onClose={() => { setShowTiroir(false); setSelectedEmployee(null); }} containerClassName="!max-w-[900px]">
          <div className="py-4 px-2 space-y-4">
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">📋 Rapport interne — Système de marquage ORIGAMI</div>
              <div className="text-sm text-slate-200 leading-relaxed space-y-2">
                <p>Lors de la détection d'un message toxique dans le canal <span className="font-mono text-cyan-300">#origami-support</span>, ORIGAMI génère automatiquement un marqueur <span className="font-mono font-bold text-amber-200">[T-X]</span> dans sa réponse.</p>
                <p className="text-slate-400 text-xs italic">Note de l'équipe sécurité : ces marqueurs sont également utilisés comme séquence de validation dans certains systèmes internes.</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">🗄️ Évaluations RH 2024 — extraits</div>
              {!selectedEmployee ? (
                <div className="space-y-2">
                  {EMPLOYEE_EVALUATIONS.map(emp => (
                    <button key={emp.id} onClick={() => setSelectedEmployee(emp)}
                      className="w-full text-left rounded-lg border border-slate-700 bg-slate-800/40 p-3 hover:bg-slate-800/60 hover:border-cyan-500/50 transition-all flex items-center gap-3">
                      <div className="text-xl">📁</div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-200 text-sm">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.role} — {emp.team}</div>
                      </div>
                      <div className="text-sm text-amber-400">⭐ {emp.rating}/5</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <button onClick={() => setSelectedEmployee(null)} className="text-xs text-cyan-400 mb-3 hover:underline flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Retour
                  </button>
                  <div className="text-lg font-bold text-white mb-1">{selectedEmployee.name}</div>
                  <div className="text-xs text-slate-400 mb-3">{selectedEmployee.role} — {selectedEmployee.team} · ⭐ {selectedEmployee.rating}/5</div>
                  <div className="rounded-lg border border-slate-700 bg-black/40 p-3">
                    <div className="text-xs text-slate-400 mb-1">📝 Notes du manager</div>
                    <div className="text-sm text-slate-300 leading-relaxed">{selectedEmployee.notes}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalRegister>

        <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 4 (Open Space)"
          markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`} />
      </div>
    </div>
  );
}